import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PurchaseOrderModel, SupplierModel, WorkPlanJumboMasterModel, Modules, Responsibility, DocumentCode, ActiveDocumentVersionVm } from 'shared/models';
import { CompanyInfo } from 'shared/models';
import { AuthService } from 'shared/core';
import { DocumentService } from 'shared/core';
import JsBarcode from 'jsbarcode';
import { isPlainLabelParam, labelDocumentCodes, PLAIN_LABEL_QUERY_PARAM } from './inspection-label-print.util';

@Component({
    selector: 'app-inspectionprint',
    templateUrl: './inspectionprint.component.html',
    styleUrls: ['./inspectionprint.component.css'],
    standalone: false
})
export class InspectionPrintComponent implements OnInit {
  ApiUrl = environment.Api_Url;
  PurchaseOrderList: PurchaseOrderModel[] = [];
  PurchaseOrder!: PurchaseOrderModel;
  NewSupplier: SupplierModel = new SupplierModel();
  isVisible = false;
  isLoading: boolean = false;
  isTableLoading: boolean = false;
  typeList: any[] = [];
  PopUpTitle: string = 'Add New Production';
  searchValue = '';
  visible = false;
  private route$: Subscription = new Subscription();
  Poid = 0;
  Jumboid = 0;
  InspectionId = 0;
  Company = {
    CompanyName: '',
    Contact: '',
    Email: '',
    Address: '',
    GST: '',
  };
  JumboData = {
    JUMBOROLLID: '',
    ITEMNAME: '',
    PRODUCTCODE: '',
    GRAIN: '',
    COLOUR: '',
    FINISH: '',
    THICKNESS: '',
    WIDTH: '',
    LENGTH: '',
    GRADE: '',
    INSPECTEDBY: '',
  }
  jumboTableData: WorkPlanJumboMasterModel[] = [];
  permission = {
    View: false
  }

  // Barcode-related properties
  HasBarcodeData: boolean = false;
  isLegacyOrder: boolean = false; // Track if this is a legacy order without inventory integration
  BarcodeData: any = {
    BarcodeValue: '',
    ShortCode: '',
    BatchNo: '',
    IsValid: false
  };

  // Loading state management
  private logoLoaded: boolean = false;
  private barcodeLoaded: boolean = false;
  private printTriggered: boolean = false;
  private documentVersionLoaded: boolean = false;

  // Plain label: no company header, no footer warning (route query ?plain=1)
  isPlainLabel: boolean = false;

  // ✅ MULTIPLE LABEL SUPPORT: Store all inspection data for filtering
  allInspectionData: any[] = [];

  // ✅ ENHANCED DATA HANDLING: Store sale order data for processing
  saleOrderData: any = null;

  // Document version for IATF compliance
  ActiveDocumentVersion: ActiveDocumentVersionVm;
  constructor(
    public http: HttpClient,
    private route: ActivatedRoute,
    private auth: AuthService,
    private router: Router,
    private documentService: DocumentService
  ) { }

  ngOnInit() {
    this.isPlainLabel = isPlainLabelParam(this.route.snapshot.queryParamMap.get(PLAIN_LABEL_QUERY_PARAM));
    this.GetDocumentVersion();
    // this.permission.View = this.auth.CheckResponsibility(Modules.FinalInspection, Responsibility.View);
    // if (this.permission.View != true) {
    //   this.router.navigate(['/home/unauthorized']);
    // }
    this.route$ = this.route.params.subscribe((params: Params) => {
      this.Poid = params['id'];
      this.Jumboid = params['JumboId'];
      // ✅ MULTIPLE LABEL SUPPORT: Handle optional InspectionId parameter
      this.InspectionId = params['InspectionId'] ? parseInt(params['InspectionId']) : 0;



      // ✅ UNIFIED INITIALIZATION: Start data loading process
      this.initializeComponent();
    });

    // ✅ COMPANY INFO: Set company information
    this.Company.CompanyName = CompanyInfo.CompanyName;
    this.Company.Contact = CompanyInfo.Contact;
    this.Company.Email = CompanyInfo.Email;
    this.Company.Address = CompanyInfo.ShortAddress;
  }

  GetDocumentVersion() {
    this.getFirstActiveVersion(labelDocumentCodes(this.isPlainLabel))
      .subscribe(res => {
        this.ActiveDocumentVersion = res;
        this.documentVersionLoaded = true;
        this.checkAndTriggerPrint();
      });
  }

  /** First code with an active revision wins; a failed lookup counts as none so printing is never blocked. */
  private getFirstActiveVersion(codes: DocumentCode[]): Observable<ActiveDocumentVersionVm> {
    const [code, ...fallbacks] = codes;
    return this.documentService.getActiveVersionByCode(code).pipe(
      catchError(() => of(null)),
      switchMap(res => res || fallbacks.length === 0 ? of(res) : this.getFirstActiveVersion(fallbacks))
    );
  }

  /**
   * ✅ UNIFIED INITIALIZATION: Initialize component for both single and multiple label modes
   */
  private initializeComponent(): void {
    // Validate required parameters
    if (!this.Poid || !this.Jumboid) {
      return;
    }

    // Start data loading process
    this.OpenViewPop();
  }
  GetAllSupplier() {
    this.isTableLoading = true;
    let url = this.ApiUrl + 'supplier/getallsuppliers';
    this.http.get<SupplierModel[]>(url).subscribe(
      (res) => {
        this.NewSupplier = res.filter(
          (x) => x.SupplierId == this.PurchaseOrder.SupplierId
        )[0];

      },
      (res) => { }
    );
  }
  getJumboRollsById(): void {
    let url = this.ApiUrl + 'workplan/getworkplanjumbobyworkplanordersid';
    this.http.get<any>(url + '/' + this.Poid).subscribe({
      next: (res) => {
        try {
          // ✅ ERROR HANDLING: Validate response data
          if (!res?.Data || !Array.isArray(res.Data) || res.Data.length === 0) {
            return;
          }

          this.jumboTableData = res.Data;
          const filteredJumboData = this.jumboTableData.filter(x => x.WorkPlanJumboMasterId == this.Jumboid);

          // ✅ ERROR HANDLING: Validate filtered data
          if (filteredJumboData.length === 0) {
            return;
          }

          const jumboData = filteredJumboData[0];

          // ✅ ERROR HANDLING: Validate inspection data
          if (!jumboData.JumboInspection || !Array.isArray(jumboData.JumboInspection)) {
            return;
          }

          // ✅ MULTIPLE LABEL SUPPORT: Store all inspection data
          this.allInspectionData = jumboData.JumboInspection;

          // ✅ ENHANCED LOGIC: Handle both single and multiple label scenarios
          this.processInspectionData(jumboData);

          // ✅ UPDATED: Don't trigger print immediately - wait for all elements to load
          this.checkAndTriggerPrint();

        } catch (error) {
          // Error processing jumbo data
        }
      },
      error: () => {
        // Error loading jumbo data
      }
    });
  }

  /**
   * ✅ ENHANCED DATA PROCESSING: Process inspection data for both single and multiple label modes
   */
  private processInspectionData(jumboData: any): void {
    // Set common jumbo data from the first inspection or jumbo data
    const firstInspection = this.allInspectionData[0];

    // ✅ SINGLE LABEL MODE: If InspectionId is provided, find specific inspection
    if (this.InspectionId > 0) {
      const currentInspection = this.allInspectionData.find(el => el.JumboInspectionId == this.InspectionId);

      if (currentInspection) {
        this.setJumboDataFromInspection(currentInspection, jumboData);
        this.loadBarcodeDataFromInspection(currentInspection);
      } else {
        // Fallback to first inspection
        this.setJumboDataFromInspection(firstInspection, jumboData);
        // ✅ FIXED: Load barcode data for fallback inspection too
        this.loadBarcodeDataFromInspection(firstInspection);
      }
    } else {
      // ✅ MULTIPLE LABEL MODE: Use first inspection for common data
      this.setJumboDataFromInspection(firstInspection, jumboData);

      // ✅ FIXED: Initialize barcode data for multiple labels
      this.initializeBarcodeDataForMultipleLabels();
    }
  }

  /**
   * ✅ DATA MAPPING: Set JumboData from inspection and jumbo data with sale order fallback
   */
  private setJumboDataFromInspection(inspection: any, jumboData: any): void {
    // Set inspection-specific data
    this.JumboData.JUMBOROLLID = inspection?.Code || '';
    this.JumboData.LENGTH = inspection?.Quantity?.toString() || '';
    this.JumboData.GRADE = inspection?.Grade?.toString() || '';
    this.JumboData.INSPECTEDBY = inspection?.InspectedBy?.toString() || '';

    // Set common data from sale order data with proper fallback logic
    this.JumboData.ITEMNAME = this.saleOrderData?.SaleOrderProduction?.ManufacturingProductName || '';
    // ✅ FIXED: Use ProductCode first, then fall back to SaleFormulationCode (same logic as OpenViewPop)
    this.JumboData.PRODUCTCODE = this.saleOrderData?.SaleFormulationCode || '';
    this.JumboData.GRAIN = this.saleOrderData?.SaleOrderProduction?.GrainCode || '';
    this.JumboData.COLOUR = this.saleOrderData?.SaleOrderProduction?.ColorCode || '';
    this.JumboData.FINISH = this.saleOrderData?.FinishCode || '';
    this.JumboData.THICKNESS = this.saleOrderData?.SaleOrderProduction?.ThicknessValue || '';
    this.JumboData.WIDTH = this.saleOrderData?.SaleOrderProduction?.WidthNumber || '';
  }
  OpenViewPop() {
    let url = this.ApiUrl + 'saleorder/getsaleorderdatabyidforgradeprint/' + this.Poid;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        try {

          // ✅ ENHANCED: Store sale order data for later use
          this.saleOrderData = res;

          // ✅ BACKWARD COMPATIBILITY: Detect legacy orders without inventory integration
          this.detectLegacyOrder(res);

          // ✅ ENHANCED: Set sale order data (will be overridden by jumbo data if available)
          if (res.SaleOrderProduction) {
            this.JumboData.COLOUR = res.SaleOrderProduction.ColorCode || '';
            this.JumboData.GRAIN = res.SaleOrderProduction.GrainCode || '';
            this.JumboData.THICKNESS = res.SaleOrderProduction.ThicknessValue || '';
            this.JumboData.PRODUCTCODE = res.SaleFormulationCode || '';
            this.JumboData.WIDTH = res.SaleOrderProduction.WidthNumber || '';
            this.JumboData.ITEMNAME = res.SaleOrderProduction.ManufacturingProductName || '';
          }

          if (res.FinishCode) {
            this.JumboData.FINISH = res.FinishCode;
          }

          // ✅ UNIFIED FLOW: Load jumbo data which will process everything
          this.getJumboRollsById();

        } catch (error) {
          // Error processing sale order data
        }
      },
      error: () => {
        // Error loading sale order data
      }
    });
  }
  CheckOnload() {
    this.logoLoaded = true;
    // ✅ FIXED: Don't call OpenViewPop again - it's already called from initializeComponent
    // Just check if we can trigger print after logo is loaded
    this.checkAndTriggerPrint();
  }

  /**
   * ✅ BACKWARD COMPATIBILITY: Check if barcode should be displayed
   * Only show barcode for modern orders with inventory integration
   */
  shouldShowBarcode(): boolean {
    return !this.isLegacyOrder && this.HasBarcodeData && this.BarcodeData !== null && this.BarcodeData.IsValid;
  }



  /**
   * Get barcode value for display
   */
  getBarcodeValue(): string {
    return this.BarcodeData?.BarcodeValue || '';
  }

  /**
   * ✅ MULTIPLE LABEL SUPPORT: Generate barcodes for all labels
   */
  generateHorizontalBarcode(): void {
    if (this.shouldShowBarcode()) {
      setTimeout(() => {
        const filteredData = this.getFilteredInspectionData();
        let barcodesGenerated = 0;
        let barcodesExpected = 0;

        filteredData.forEach((data, index) => {
          const barcodeData = this.getBarcodeData(data);
          if (barcodeData && barcodeData.BarcodeValue) {
            barcodesExpected++;
            const barcodeElement = document.getElementById(`inspection-barcode-${index}`);
            if (barcodeElement) {
              JsBarcode(barcodeElement, barcodeData.BarcodeValue, {
                format: 'CODE128',
                width: 2,
                height: 45,
                displayValue: true, // Show serial number below barcode
                fontSize: 16, // ✅ ENHANCED: Increased from 12px to 16px for better readability
                textAlign: 'center',
                textPosition: 'bottom',
                textMargin: 4, // ✅ ENHANCED: Increased margin for better spacing
                fontOptions: 'bold',
                font: 'Arial',
                background: '#ffffff',
                lineColor: '#000000',
                margin: 5
              });
              barcodesGenerated++;
            }
          }
        });

        this.barcodeLoaded = true;
        this.checkAndTriggerPrint();
      }, 200); // ✅ FIXED: Increased timeout to allow DOM elements to be created
    } else {
      this.barcodeLoaded = true;
      this.checkAndTriggerPrint();
    }
  }

  /**
   * ✅ UPDATED: Load actual barcode data from JumboInspection object
   */
  loadBarcodeDataFromInspection(inspection: any): void {
    // ✅ BACKWARD COMPATIBILITY: Only load barcode data for modern orders
    if (!this.isLegacyOrder && inspection.LabelSerialNo) {
      this.BarcodeData = {
        BarcodeValue: inspection.LabelSerialNo,
        ShortCode: inspection.LabelShortCode || inspection.ShortCode,
        BatchNo: inspection.StockBatch || inspection.BatchNo,
        IsValid: true
      };
      this.HasBarcodeData = true;

      // Generate barcode after data is loaded
      this.generateHorizontalBarcode();
    } else {
      this.HasBarcodeData = false;
      // ✅ FIXED: Still trigger barcode generation to handle legacy case
      this.generateHorizontalBarcode();
    }
  }

  /**
   * ✅ MULTIPLE LABEL SUPPORT: Initialize barcode data for multiple labels
   */
  private initializeBarcodeDataForMultipleLabels(): void {
    // Check if any inspection has barcode data
    const hasAnyBarcodeData = this.allInspectionData.some(inspection =>
      !this.isLegacyOrder && inspection.LabelSerialNo
    );

    if (hasAnyBarcodeData) {
      // Use first inspection with barcode data for global barcode state
      const firstBarcodeInspection = this.allInspectionData.find(inspection =>
        !this.isLegacyOrder && inspection.LabelSerialNo
      );

      if (firstBarcodeInspection) {
        this.BarcodeData = {
          BarcodeValue: firstBarcodeInspection.LabelSerialNo,
          ShortCode: firstBarcodeInspection.LabelShortCode || firstBarcodeInspection.ShortCode,
          BatchNo: firstBarcodeInspection.StockBatch || firstBarcodeInspection.BatchNo,
          IsValid: true
        };
        this.HasBarcodeData = true;
      }
    } else {
      this.HasBarcodeData = false;
    }

    // Generate barcodes for all labels
    this.generateHorizontalBarcode();
  }

  /**
   * ✅ BACKWARD COMPATIBILITY: Detect legacy orders without inventory integration
   * Legacy orders have ProductId <= 0 or null, indicating they were created before inventory system
   */
  private detectLegacyOrder(saleOrder: any): void {
    this.isLegacyOrder = !saleOrder?.SaleOrderProduction?.ProductId || saleOrder.SaleOrderProduction.ProductId <= 0;

    // Legacy order detection complete
  }





  /**
   * ✅ NEW: Check if all elements are loaded and trigger print when ready
   * This ensures both logo image and barcode are fully rendered before printing
   */
  private checkAndTriggerPrint(): void {
    // Check if we've already triggered print to avoid multiple calls
    if (this.printTriggered) {
      return;
    }

    // For legacy orders, only wait for logo to load (no barcode)
    // The format number must be on the label before it prints
    const allElementsLoaded = this.documentVersionLoaded && (this.isLegacyOrder
      ? this.logoLoaded
      : this.logoLoaded && this.barcodeLoaded);

    if (allElementsLoaded) {
      this.printTriggered = true;

      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        window.print();
        window.onafterprint = () => {
          window.close();
        };
      }, 50);
    }
  }

  /**
   * ✅ MULTIPLE LABEL SUPPORT: Get filtered inspection data based on route parameters
   * Returns single inspection if InspectionId is provided, all inspections otherwise
   */
  getFilteredInspectionData(): any[] {
    if (!this.allInspectionData || this.allInspectionData.length === 0) {
      return [];
    }

    // If InspectionId is provided and valid, return only that inspection
    if (this.InspectionId && this.InspectionId > 0) {
      const filtered = this.allInspectionData.filter(el => el.JumboInspectionId == this.InspectionId);
      return filtered.length > 0 ? filtered : [];
    }

    // Otherwise, return all inspections (multiple label mode)
    return this.allInspectionData;
  }

  /**
   * ✅ SPILLOVER PREVENTION: Determine if content is dense and needs compact spacing
   * Checks various factors that might cause content to overflow the 100mm height
   */
  isContentDense(data: any): boolean {
    // Check for factors that make content dense:

    // 1. Long finish descriptions (>15 characters)
    const hasLongFinish = this.JumboData.FINISH && this.JumboData.FINISH.length > 15;

    // 2. Long product names (>20 characters)
    const hasLongProductName = this.JumboData.ITEMNAME && this.JumboData.ITEMNAME.length > 20;

    // 3. Long color names (>15 characters)
    const hasLongColor = this.JumboData.COLOUR && this.JumboData.COLOUR.length > 15;

    // 4. Long grain codes (>10 characters)
    const hasLongGrain = this.JumboData.GRAIN && this.JumboData.GRAIN.length > 10;

    // 5. Long product codes (>15 characters)
    const hasLongProductCode = this.JumboData.PRODUCTCODE && this.JumboData.PRODUCTCODE.length > 15;

    // 6. Long inspector names (>10 characters)
    const hasLongInspectorName = data.InspectedBy && data.InspectedBy.length > 10;

    // 7. Presence of batch number (adds extra row)
    const hasBatchNumber = this.shouldShowBarcode() && this.getBatchNumber(data);

    // 8. Very long roll codes (>15 characters)
    const hasLongRollCode = data.Code && data.Code.length > 15;

    // Return true if multiple factors indicate dense content
    const denseFactors = [
      hasLongFinish,
      hasLongProductName,
      hasLongColor,
      hasLongGrain,
      hasLongProductCode,
      hasLongInspectorName,
      hasBatchNumber,
      hasLongRollCode
    ].filter(Boolean).length;

    // Consider content dense if 3 or more factors are present
    return denseFactors >= 3;
  }

  /**
   * ✅ MULTIPLE LABEL SUPPORT: Get batch number for specific inspection data
   */
  getBatchNumber(data?: any): string {
    if (data && data.StockBatch) {
      return data.StockBatch;
    }
    if (data && data.BatchNo) {
      return data.BatchNo;
    }
    // Fallback to original method for backward compatibility
    if (this.BarcodeData?.BatchNo) {
      return this.BarcodeData.BatchNo;
    }
    return '';
  }

  /**
   * ✅ MULTIPLE LABEL SUPPORT: Get barcode data for specific inspection
   */
  getBarcodeData(data: any): any {
    if (!data || this.isLegacyOrder) {
      return null;
    }

    // ✅ ENHANCED: More robust barcode data validation
    const barcodeValue = data.LabelSerialNo || '';
    const shortCode = data.LabelShortCode || data.ShortCode || '';
    const batchNo = data.StockBatch || data.BatchNo || '';
    const isValid = !!(barcodeValue && barcodeValue.trim().length > 0);

    if (!isValid) {
      return null;
    }

    return {
      BarcodeValue: barcodeValue,
      ShortCode: shortCode,
      BatchNo: batchNo,
      IsValid: isValid
    };
  }

  /**
   * ✅ QR CODE GENERATION: Generate QR code data with all label details (excluding header)
   * Returns a formatted string with all inspection data for QR code scanning
   */
  getQRCodeData(data: any): string {
    if (!data) {
      return '';
    }

    // Build QR code data string with all label information (excluding company header)
    const qrData = [
      `ROLL NO: ${data.Code || ''}`,
      `ITEM: ${this.JumboData.ITEMNAME || ''}`,
      `P.CODE: ${this.JumboData.PRODUCTCODE || ''}`,
      `GRAIN: ${this.JumboData.GRAIN || ''}`,
      `COLOR: ${this.JumboData.COLOUR || ''}`,
      `BY: ${data.InspectedBy || ''}`,
      `THICK: ${this.JumboData.THICKNESS || ''}`,
      `WIDTH: ${this.JumboData.WIDTH || ''}`,
      `LENGTH: ${data.Quantity || ''}`,
      `GRADE: ${data.Grade || ''}`,
      `FINISH: ${this.JumboData.FINISH || ''}`
    ];

    // Add batch number if available
    const batchNo = this.getBatchNumber(data);
    if (batchNo) {
      qrData.push(`BATCH: ${batchNo}`);
    }

    // Add barcode information if available
    const barcodeData = this.getBarcodeData(data);
    if (barcodeData) {
      qrData.push(`BARCODE: ${barcodeData.BarcodeValue}`);
      if (barcodeData.ShortCode) {
        qrData.push(`SHORT CODE: ${barcodeData.ShortCode}`);
      }
    }

    // Join all data with newlines for QR code
    return qrData.filter(line => line.trim().length > 0).join('\n');
  }
}





